import cx from 'classnames';
import React, { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { usePatronSelector } from '../../../common/use-patron-selector';
import useScreenSize from '../../../common/use-screen-size';
import { CommentAssistantSuggestionSelection } from '../../../models/comments/types';
import { Feature, getFeatureFlag } from '../../../models/features/features';
import { Button, SubmitCommentIcon } from '../../ui';
import CommentAssistantButton from './comment-assistant-button';
import { CommentAssistantDropdownMenu } from './comment-assistant-menus/dropdown-menu';
import CommentFormActions from './comment-form-actions';

interface CommentFormFooterProps {
  commentLength: number;
  commentLengthMax: number;
  disabled: boolean;
  suggestionDisabled: boolean;
  replyLevel?: number;
  onSubmit: () => void;
  onEmojiIconClick: () => void;
  onMentionIconClick: () => void;
  onAttachmentClick: (file: File) => void;
  onSuggestionSelected: (
    suggestion: CommentAssistantSuggestionSelection
  ) => void;
  // temporary solution until we have converted the `CommentForm` component to a functional component
  setIsAssistantMobileMenuOpen?: Dispatch<SetStateAction<boolean>>;
}

const CommentFormFooter: React.FC<CommentFormFooterProps> = ({
  commentLength,
  commentLengthMax,
  disabled,
  suggestionDisabled,
  replyLevel = 0,
  onSubmit,
  onEmojiIconClick,
  onMentionIconClick,
  onAttachmentClick,
  onSuggestionSelected,
  setIsAssistantMobileMenuOpen,
}) => {
  const { t } = useTranslation();
  const replyInPlaceEnabled = usePatronSelector((state) =>
    getFeatureFlag(state, Feature.REPLY_IN_PLACE_ENABLED)
  );
  const commentAssistantEnabled = usePatronSelector((state) =>
    getFeatureFlag(state, Feature.COMMENT_ASSISTANCE_ENABLED)
  );
  const { isTabletOrSmaller, isMobile, width } = useScreenSize();

  const toggleAssistantMobileMenu = () => {
    setIsAssistantMobileMenuOpen?.((prevOpen) => !prevOpen);
  };

  const handleSuggestionSelected = (
    selection: CommentAssistantSuggestionSelection
  ) => {
    onSuggestionSelected(selection);
  };

  const counterClassName = cx('counter', {
    'counter--error': commentLength > commentLengthMax,
  });

  //hand-picked breakpoints for the 3 layers of reply (0, 1, 2)
  let buttonsOutsideMenu = 3;
  if (replyInPlaceEnabled && isMobile && replyLevel > 0) {
    if (replyLevel === 1) {
      if (width <= 425) {
        buttonsOutsideMenu = 2;
      }
    } else if (replyLevel === 2) {
      if (width <= 500 && width > 450) {
        buttonsOutsideMenu = 2;
      } else if (width <= 450) {
        buttonsOutsideMenu = 1;
      }
    }
  }

  return (
    <>
      <div className="comment-form-footer">
        <div className="actions">
          <CommentFormActions
            onEmojiIconClick={onEmojiIconClick}
            onMentionIconClick={onMentionIconClick}
            onAttachmentClick={onAttachmentClick}
            buttonsOutsideMenu={buttonsOutsideMenu}
          />
          {commentAssistantEnabled ? (
            !isTabletOrSmaller ? (
              <CommentAssistantDropdownMenu
                disabled={suggestionDisabled}
                onSuggestionSelected={handleSuggestionSelected}
              />
            ) : (
              <CommentAssistantButton
                disabled={suggestionDisabled}
                onClick={toggleAssistantMobileMenu}
              />
            )
          ) : null}
        </div>

        <div className="submit">
          <span
            role="status"
            aria-label="Character count"
            className={counterClassName}
          >
            {commentLength}/{commentLengthMax}
          </span>
          <Button
            type="submit"
            disabled={disabled}
            onClick={onSubmit}
            aria-label={t('common.submit')}
          >
            <SubmitCommentIcon />
          </Button>
        </div>
      </div>
    </>
  );
};

export default CommentFormFooter;
